import React, { useCallback } from 'react';

import { BuiltInComponents } from 'craft/constants';
import { Section } from 'craft/containers/Toolbox/components';
import { ObjectFit } from 'craft/containers/Toolbox/sections/ObjectFit';
import { Overflow } from 'craft/containers/Toolbox/sections/Overflow';
import { ResponsiveSize } from 'craft/containers/Toolbox/sections/Size/ResponsiveSize';
import { useMultiNodes, useResponsiveCss } from 'craft/hooks';
import { useIsUsingDefaultCssControls } from 'craft/state';
import { ToolboxComponentSchema } from 'craft/types';

interface Props {
  nodeIds: string[];
  schema: ToolboxComponentSchema['Size'];
}

export const SizeToolboxSection = React.memo(({ nodeIds, schema }: Props) => {
  const isUsingDefaultCssControls = useIsUsingDefaultCssControls();
  const {
    cssSelectorId,
    hasMinMaxSize,
    hasAutoSize,
    hasPercentageSize,
    hasOverflow,
    hasObjectFit,
  } = schema;
  const { responsiveCss, setResponsiveCss } = useResponsiveCss(nodeIds, undefined, cssSelectorId);
  const { type } = useMultiNodes(nodeIds);

  const onSetFixedHeight = useCallback(
    (property) => {
      // if we are setting a fixed height for a component that has an unset overflow
      // set overflow to auto automatically
      if (
        type === BuiltInComponents.View &&
        property === 'height' &&
        hasOverflow &&
        !responsiveCss.overflow
      ) {
        setResponsiveCss({ ...responsiveCss, overflow: 'auto' });
      }
    },
    [hasOverflow, responsiveCss, setResponsiveCss, type],
  );

  return (
    <Section name="Size" data-cy="toolbox-section-size">
      <ResponsiveSize
        property="normal"
        nodeIds={nodeIds}
        supportsAutoSize={hasAutoSize}
        supportsPercentageSize={hasPercentageSize}
        cssSelectorId={cssSelectorId}
        onSetFixedSize={onSetFixedHeight}
      />
      {hasMinMaxSize && (
        <>
          <ResponsiveSize
            property="min"
            nodeIds={nodeIds}
            supportsPercentageSize={hasPercentageSize}
            cssSelectorId={cssSelectorId}
          />
          <ResponsiveSize
            property="max"
            nodeIds={nodeIds}
            supportsPercentageSize={hasPercentageSize}
            cssSelectorId={cssSelectorId}
          />
        </>
      )}

      {hasOverflow && <Overflow nodeIds={nodeIds} cssSelectorId={cssSelectorId} />}

      {isUsingDefaultCssControls && hasObjectFit && <ObjectFit nodeIds={nodeIds} />}
    </Section>
  );
});

SizeToolboxSection.displayName = 'SizeToolboxSection';
